import {
  ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, OnDestroy,
  ViewChild, Output, EventEmitter } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Subscription ,  Observable } from 'rxjs';
import { FormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { BrandService } from 'shared/brand/brand.service';
import { ItemDetailService } from 'shared/item-detail/item-detail.service';
import { LookupsService } from 'shared/lookups/lookups.service';
import { ProductGroupsService } from 'shared/product-groups/product-groups.service';
import { CoreService } from 'shared/global-core/core.service';
import { Detail } from '../../models/detail.model';
import { segmentsData } from '../../../product-details/segments-data-stub';
import { fieldsInfo } from './fields-info';
import { BaseSegmentComponent } from '../base/_base-segment.component';
import { ProductPanelService } from '../../product-panel.service';
import { SearchService } from 'shared/search/search.service';
import * as utils from 'utils/utils';
import * as dateUtils from 'utils/date';
import * as _ from 'lodash';
import { ECOMMERCE_PANEL } from '../../../product-panel/product-panel-types';

import { TypeaheadMatch } from 'ngx-bootstrap/typeahead';

import { createOption } from 'shared/form/select/option-helpers';
import {mergeMap} from 'rxjs/operators';

@Component({
  moduleId: module.id,
  selector: 'item-details-segment',
  templateUrl: 'item-details.component.html',
  styleUrls: ['item-details.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ItemDetailsComponent extends BaseSegmentComponent implements OnInit, OnDestroy {
  public typeaheadLoading: boolean;

  public typeaheadNoResults: boolean;

  @Output() partTypeClick: EventEmitter<any> = new EventEmitter<any>();
  @Output() forceSave: EventEmitter<any> = new EventEmitter();

  detailFormGroup: UntypedFormGroup;
  detail: Detail;
  declare data: any[];
  mainData: any[];
  showMoreData: any[];
  coreInfoData: any[];
  warrantyData: any[];
  partHistoryData: any[];
  productGroupsData: Array<any>;
  fieldsInfo: any = fieldsInfo;
  showMore: boolean = false;
  segmentName: string = 'item-details';
  panelTypeFields: any = {
    [ECOMMERCE_PANEL]: [
      'partNumber', 'details.brandLabel', 'partTerminology.id',
      'partTerminology.name', 'partTerminology.category.name',
      'partTerminology.subcategory.name', 'details.quantityPerApplication',
      'details.quantityPerApplicationUom', 'details.warrantyDistance',
      'details.warrantyDistanceUom', 'details.warrantySpecial',
      'details.warrantySpecialUom', 'details.warrantyTime',
      'details.warrantyTimeUom'
    ],
  };
  loadCoreClassSub: Subscription;
  lookupsServiceSub: Subscription;
  productGroupsServiceSub: Subscription;

  get segmentForm(): UntypedFormGroup {
    return this.detailFormGroup;
  }

  constructor(
    protected productPanelService: ProductPanelService,
    protected brandService: BrandService,
    protected itemDetailService: ItemDetailService,
    protected lookupsService: LookupsService,
    protected searchService: SearchService,
    protected route: ActivatedRoute,
    protected ref: ChangeDetectorRef,
    private productGroupsService: ProductGroupsService,
    private coreService: CoreService,
    private fb: UntypedFormBuilder
  ) {
    super(productPanelService, brandService, itemDetailService, searchService, route, ref);
  }

  get category() {
    return this.detailFormGroup.controls.partCategory.value;
  }

  get subCategory() {
    return this.detailFormGroup.controls.partSubcategory.value;
  }

  set category(category: string) {
    this.detailFormGroup.controls.partCategory.setValue(category);
  }

  set subCategory(subcategory: string) {
    this.detailFormGroup.controls.partSubcategory.setValue(subcategory);
  }

  set partType(partType: string) {
    this.detailFormGroup.controls.partType.setValue(partType);
  }

  set partTypeName(partTypeName: string) {
    this.detailFormGroup.controls.partTypeName.setValue(partTypeName);
  }

  ngOnInit() {
    let data = this.getPanelSegmentsData(segmentsData.items_details);
    if (this.panelType === ECOMMERCE_PANEL) { this.showMore = true; }
    // change placeholders for bulkEdit mode
    if (this.bulkEdit) {
      data = this.getSegmentsWithPlaceholder(segmentsData.items_details);
    }
    this.data = data;
    this.separateData(this.data);
    /*
      Initialize detail form
    */
    this.detail = new Detail();
    _.each(this.data, (d: any) => {

      if (d.field_map) {
        let validators: Array<any> = [];

        if (_.has(d, 'validation.PIES_6_5' )) {
          validators = this.initValidators(d.validation.PIES_6_5, this);
        }

        _.set(this.detail, d.field_map, new UntypedFormControl(_.get(d, 'default_value', ''),  validators) );

        if (_.has(d, 'api_lookup.lookup')) {
          _.set(this.detail, d.api_lookup.field_map, new UntypedFormControl(_.get(d.api_lookup, 'default_value', '')) );
          d.dataSource = new Observable((observer: any) => {
              // Runs on every search
              observer.next((<any>this.detail)[d.api_lookup.field_map].value);
            })
            .pipe(mergeMap((token: string) => {
              return this.lookupsService.fetch(d.api_lookup.lookup, token);
            }));
        }
      }

    });



    this.detailFormGroup = this.fb.group(this.detail);
    /*
     Subscribe to Item Detail service
    */
    this.subscribeToItemDetail();
  }

  ngOnDestroy() {
    if (this.loadCoreClassSub) { this.loadCoreClassSub.unsubscribe(); }
    if (this.lookupsServiceSub) { this.lookupsServiceSub.unsubscribe(); }
    if (this.productGroupsServiceSub) { this.productGroupsServiceSub.unsubscribe(); }
    super.ngOnDestroy();
  }

  save(): Observable<any> {
    if (this.bulkEdit) {
      return this.putDataBulk(
        this.segmentName,
        this.detailFormGroup.value
      );
    } else {
      const { id } = this.route.snapshot.params;
      return this.putData(id, this.segmentName, this.detailFormGroup.value);
    }
  }

  fieldMaxLength(item: any): number {
    return 20;
  }


  fieldCellSize(field_map: string): string {
    return utils.getFieldCellSize(field_map, this.fieldsInfo);
  }

  hasCharCounter(item: any): boolean {
    // set to true to add char counter to the field
    return false;
  }

  switchField(item: any): string {
    if (this.bulkEdit && item.not_bulk_editable) { return 'readonly'; }
    return utils.switchField(item.field_map, this.fieldsInfo);
  }

  // replace parent method:
  setBulkData() {
    this.setData({
      details: this.getBulkSegmentData(this.segmentName)
    });
  }

  // replace parent method:
  handleItemDetail(itemDetail: any) {
    this.itemDetailsData = itemDetail;
    this.setData(itemDetail);
  }

  setData(itemDetail: any = {}): void {
    // warrantyDistanceUom/warrantyTimeUom have only one possible values Miles/Months
    _.set(itemDetail, 'details.warrantyDistanceUom', 'MI');
    _.set(itemDetail, 'details.warrantyTimeUom', 'MO');
    _.each(this.data, (o: any) => {
      if (o.field_map) {
        this.detailFormGroup.controls[o.field_map].setValue(_.get(itemDetail, o.item_name, ''));
        this.ref.markForCheck();
        if (o.api_lookup) {
          if (o.api_lookup.type === 'typeahead') {
            this.detailFormGroup.controls[o.api_lookup.field_map].setValue(_.get(itemDetail, o.api_lookup.item_name, ''));
            this.ref.markForCheck();
          } else {
            this.lookupsServiceSub = this.lookupsService.fetch(o.api_lookup.lookup, _.get(itemDetail, o.api_lookup.field_map, ''))
              .subscribe((lookup) => {
                o.lookup = this.addLookupPlaceholder(lookup);
                this.ref.markForCheck();
              });
          }
        }
      }
    });
    this.loadProductGroups(_.get(itemDetail, 'details.group.id', null));
    this.loadCoreClass(_.get(itemDetail, 'details.core', null));
    // set part type from local storage in bulk edit mode
    if (this.detailFormGroup.get('availableDate')) {
      this.setDateDisabledRange(this.detailFormGroup.get('availableDate').value, 'details.effectiveDate');
      this.setDateAheadControlError(
        this.detailFormGroup.get('effectiveDate'),
        this.detailFormGroup.get('availableDate')
      );
    }
    if (this.bulkEdit) {
      this.setBulkPartType(itemDetail);
    }
  }

  updatePartType(partType: any) {
    this.partType = partType.part_terminology_id;
    this.partTypeName = partType.part_terminology_name;
    this.category = partType.category_name;
    this.subCategory = partType.sub_category_name;
    this.markAsDirty();
    // this.forceSave.emit();
  }

  setBulkPartType(data: any = {}) {
    if (!data.details) { return; }
    const { partType, partTypeName, partCategory, partSubcategory } = data.details;
    this.partType = partType;
    this.partTypeName = partTypeName;
    this.category = partCategory;
    this.subCategory = partSubcategory;
  }

  // handlers
  changeTypeaheadLoading(e: boolean): void {
    this.typeaheadLoading = e;
  }

  changeTypeaheadNoResults(e: boolean, field_map: string): void {
    this.typeaheadNoResults = e;
     this.detailFormGroup.controls[field_map].setValue(null);
  }

  onSelectTypeahead(e: TypeaheadMatch, field_map: string): void {
     this.detailFormGroup.controls[field_map].setValue(e.item.id);
  }

  addAnotherClick(item: any) {
    console.log('add another', item);
  }

  onBlurTypeahead(e: any, hiddenField: string, displayField: string) {
    if (!this.detailFormGroup.controls[hiddenField].value) {
      this.detailFormGroup.controls[displayField].setValue('');
    }
  }

  // handle selects:
  onSelectChange(value: any, item: any) {
    switch (item.field_map) {
      case 'productGroupId':
        this.handleProductGroupsSelect(value.option);
        break;
      case 'coreId':
        this.setCoreGroup(value.option, item);
        break;
      default:
        break;
    }
    this.markAsDirty();
  }

  onPartTypeClick() {
    let partTerminology = null;
    if (!this.bulkEdit) {
      const data = this.itemDetailsData.partTerminology;
      if (data && data.id) {
        partTerminology = {
          part_terminology_id: data.id,
          part_terminology_name: data.name,
          category_name: data.category.name,
          sub_category_name: data.subcategory.name,
        };
      }
    }
    this.partTypeClick.emit({partTerminology, bulkEdit: this.bulkEdit});
  }

  onDateChange(selectedDate: string, item: any) {
    if (item.item_name === 'details.availableDate') {
      dateUtils.handleDateAheadChange(
        this.detailFormGroup.get('effectiveDate'),
        'details.effectiveDate',
        selectedDate,
        this.data
      );
    }
  }

  // private
  private separateData(data: any[]): void {
    const coreInfoData: any = _.groupBy(data, (item: any) => {
      return _.includes(this.fieldsInfo.coreInfoFields, item.field_map);
    });
    const warrantyData: any = _.groupBy((<any>coreInfoData['false']), (item: any) => {
      return _.includes(this.fieldsInfo.warrantyFields, item.field_map);
    });
    const partHistoryData: any = _.groupBy((<any>warrantyData['false']), (item: any) => {
      return _.includes(this.fieldsInfo.partHistoryFields, item.field_map);
    });
    const showMoreData = _.groupBy((<any>partHistoryData)['false'], (item: any) => item.show_more);

    this.mainData = (<any>showMoreData)['true'];
    this.showMoreData = (<any>showMoreData)['false'];
    this.coreInfoData = (<any>coreInfoData)['true'];
    this.warrantyData = (<any>warrantyData)['true'];
    this.partHistoryData = (<any>partHistoryData)['true'];
  }

  private loadProductGroups(productGroupId: number): void {
    // don't fetch core for ecommerce panel type
    if (this.panelType === ECOMMERCE_PANEL) { return; }
    this.productGroupsServiceSub = this.productGroupsService.fetch(this.brandService.getBrand())
    .subscribe((data: any) => {
      this.productGroupsData = data;
      const productGroup = utils.getFieldByName('details.group.id', this.data);
      productGroup.lookup = this.getProductGroupLookup(data);
      this.setProductSubGroupLookup(data, productGroupId);
      this.ref.markForCheck();
    });
  }

  private getProductGroupLookup(data: any): any {
    return _.reduce(data, (memo: Array<any>, item: any) => {
      memo.push(createOption(item.id, `${item.name} (${item.code})`));
      return memo;
    }, [this.optionPlaceholder]);
  }

  private setProductSubGroupLookup(data: any, productGroupId: any): void {
    const productGroup: any = _.find(data, (item: any) => {
      if (typeof productGroupId === 'string') {
        productGroupId = parseInt(productGroupId);
      }
      return item.id === productGroupId;
    });
    const productSubGroupSegment = utils.getFieldByName('details.group.subGroup.id', this.data);
    if (_.has(productGroup, 'product_sub_group')) {
      productSubGroupSegment.lookup = this.getProductGroupLookup(productGroup.product_sub_group);
    } else {
      productSubGroupSegment.lookup = [];
    }
    this.ref.markForCheck();
  }

  private handleProductGroupsSelect(value: number): void {
    this.setProductSubGroupLookup(this.productGroupsData, value);
    this.detailFormGroup.get('productSubGroupId').reset();
    this.ref.markForCheck();
  }

  private loadCoreClass(core: any): void {
    // don't fetch core for ecommerce panel type
    if (this.panelType === ECOMMERCE_PANEL) { return; }
    this.loadCoreClassSub = this.coreService.fetch(this.brandService.getBrand())
    .subscribe((data: any) => {
      const coreClass = utils.getFieldByName('details.core.id', this.data);
      coreClass.lookup = this.getCoreClassLookup(data);
      this.ref.markForCheck();
      this.setCoreGroup(_.get(core, 'id', 0), coreClass);
    });
  }

  private setCoreGroup(value: string | number, item: any): void {
    value = _.isString(value) ? parseInt(value) : value;
    const coreClass = _.find(item.lookup, (coreItem: any) => coreItem.option === value);
    this.detailFormGroup.get('coreGroup').setValue(_.get(coreClass, 'group', ''));
    this.detailFormGroup.get('coreReturnDaysToExpiry').setValue(_.get(coreClass, 'days', ''));
    this.ref.markForCheck();
  }

  private getCoreClassLookup(data: any): any {
    return _.reduce(data, (memo: Array<any>, item: any) => {
      memo.push({
        option: item.id,
        title: item.class,
        group: item.group,
        days: item.days
      });
      return memo;
    }, [this.optionPlaceholder]);
  }

  private isHidden(field_map: string) {
   return (this.fieldsInfo.hiddenFields && _.includes(fieldsInfo.hiddenFields, field_map));
  }
}
